import { DataWidget } from 'types/dashboard_widget';

import { WIDGET_REFETCH_INTERVAL } from 'dashboards/services/interval';

import { getDimensionsFromMetricsResponse } from 'dashboards/services/metric';
import { sanitizeDataWidgetFilters } from 'dashboards/components/DataWidgetFiltersInput/sanitize';
import { useCounter } from 'dashboards/hooks/useCounter';
import { useUserMetrics } from 'dashboards/hooks/useUserMetrics';
import { useTimeRangeParams } from 'dashboards/hooks/useTimeRangeParams';
import { useTopLevelDataWidgetFilters } from 'dashboards/hooks/useTopLevelDataWidgetFilters';

import { ChartLoader } from 'dashboards/components/ChartLoader/ChartLoader';
import { ChartError } from 'dashboards/components/ChartError/ChartError';
import { Widget } from 'dashboards/widgets/Widget/Widget';

type CounterProps = {
  widget: DataWidget<'counter'>;
};

export function Counter({ widget }: CounterProps) {
  const { metric } = widget.query;

  const { start, stop, timezone } = useTimeRangeParams();

  const { data: userMetrics } = useUserMetrics();

  const columns = getDimensionsFromMetricsResponse(metric, userMetrics);

  const filters = useTopLevelDataWidgetFilters({
    filters: sanitizeDataWidgetFilters(widget.query.filters),
    columns,
  });

  const { data, isLoading, error } = useCounter({
    params: {
      metricName: metric,
      timeRange: { start, stop },
      timeZone: timezone,
      filters,
    },
    refetchInterval: WIDGET_REFETCH_INTERVAL,
    keepPreviousData: true,
  });

  if (error) {
    return (
      <Widget widget={widget}>
        <ChartError />
      </Widget>
    );
  }

  if (!data && isLoading) {
    return (
      <Widget widget={widget}>
        <ChartLoader />
      </Widget>
    );
  }

  if (!data) {
    return null;
  }

  const value = Number(data.value);

  return (
    <Widget widget={widget}>
      <div className="flex h-full w-full items-center justify-center">
        <span className="overflow-hidden text-4xl leading-normal text-ellipsis whitespace-nowrap text-neutral-900 transition-opacity dark:text-neutral-100">
          {!isNaN(value) ? Math.round(value) : (data.value ?? '-')}
        </span>
      </div>
    </Widget>
  );
}
